// [2026-10-07] ADDED — INC-279: IncAssist "Live transcript" tab. Pure list logic (no React, no redux, no
// network), so it can be tested alone with node.
//
// A line is { id, src, role, name, text, time, final }:
//   src 'db'   — a message saved by the 5-second chunk transcript (LiveTranscription), about 10 s behind;
//   src 'live' — a turn from the real-time stream in the interviewer's browser (words within about 1 s).
// The backend merges, re-dates and joins saved messages as late chunks arrive, so saved lines are never
// patched one by one: a snapshot replaces every saved line from its first message onwards.

export const TRANSCRIPT_MAX_LINES = 400; // newest lines kept in memory (a long interview stays light)
export const TRANSCRIPT_MAX_LINE_CHARS = 6000; // one line is cut at this length
export const TRANSCRIPT_ROLES = ['interviewer', 'candidate'];

const toTime = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(time) ? time : NaN;
};

const sortAndCap = (lines) =>
    lines
        .map((line, index) => ({ line, index }))
        .sort((a, b) => a.line.time - b.line.time || a.index - b.index)
        .map((entry) => entry.line)
        .slice(-TRANSCRIPT_MAX_LINES);

// A saved message → a line, or null when it is not something to show (other roles, empty text, bad time).
export function dbLine(message) {
    const role = String(message?.role || '').toLowerCase();
    const text = String(message?.message || '').trim();
    const time = toTime(message?.dateTime);
    if (!TRANSCRIPT_ROLES.includes(role) || !text || Number.isNaN(time)) return null;
    return {
        id: `db:${role}:${time}`,
        src: 'db',
        role,
        name: String(message?.username || '').trim(),
        text: text.slice(0, TRANSCRIPT_MAX_LINE_CHARS),
        time,
        final: true,
    };
}

// Two saved messages of one speaker can carry the very same time. Each keeps its own line (the push event's
// `total` counts both), so the second one gets a numbered id.
const distinctIds = (lines) => {
    const seen = new Map();
    return lines.map((line) => {
        const count = (seen.get(line.id) || 0) + 1;
        seen.set(line.id, count);
        return count === 1 ? line : { ...line, id: `${line.id}:${count}` };
    });
};

// The whole saved transcript (GET getFinalTranscript): replaces every saved line, keeps live lines.
export function applyHistory(lines, messages) {
    const saved = distinctIds((Array.isArray(messages) ? messages : []).map(dbLine).filter(Boolean));
    return sortAndCap([...saved, ...(lines || []).filter((line) => line.src !== 'db')]);
}

// The push event `incassist-transcript`: { total, tail: [newest saved messages] }. Every saved line from the
// first tail message onwards is replaced by the tail. `gap` = the list no longer adds up to `total`, so the
// caller should reload the history (a missed event, or a change further back than the tail).
export function applySnapshot(lines, snapshot) {
    const current = lines || [];
    const tail = distinctIds((Array.isArray(snapshot?.tail) ? snapshot.tail : []).map(dbLine).filter(Boolean));
    if (!tail.length) return { lines: current, gap: false };
    const from = Math.min(...tail.map((line) => line.time));
    const next = sortAndCap([...current.filter((line) => line.src !== 'db' || line.time < from), ...tail]);
    const total = Number(snapshot?.total);
    const saved = next.filter((line) => line.src === 'db').length;
    const gap = Number.isFinite(total) && total <= TRANSCRIPT_MAX_LINES && saved !== total;
    return { lines: next, gap };
}

// One real-time turn. The same id is sent again as the words grow (final: false) and once more when the
// turn ends (final: true); each update replaces the one before.
export function upsertLive(lines, line) {
    const role = String(line?.role || '').toLowerCase();
    const text = String(line?.text || '').trim();
    const time = toTime(line?.time);
    if (!line?.id || !TRANSCRIPT_ROLES.includes(role) || Number.isNaN(time)) return lines || [];
    const current = lines || [];
    // An empty update for a turn that is not on screen yet is nothing to show.
    if (!text && !current.some((existing) => existing.id === line.id)) return current;
    const next = {
        id: String(line.id),
        src: 'live',
        role,
        name: String(line.name || '').trim(),
        text: text.slice(0, TRANSCRIPT_MAX_LINE_CHARS),
        time,
        final: !!line.final,
    };
    const without = current.filter((existing) => existing.id !== next.id);
    // A finished turn with no words (noise) is dropped instead of leaving an empty bubble.
    return sortAndCap(next.text ? [...without, next] : without);
}

// When the real-time stream stops for good, its lines are dropped and the saved transcript shows everything.
export function dropLive(lines) {
    return (lines || []).filter((line) => line.src !== 'live');
}

// Which lines are on screen. While the real-time stream runs (`liveFrom` = when it started), saved lines from
// that moment on are hidden: the stream already shows those words, and showing both would repeat them.
export function visibleLines(lines, liveFrom) {
    const hasLiveFrom = Number.isFinite(liveFrom);
    return (lines || []).filter((line) => line.src === 'live' || !hasLiveFrom || line.time < liveFrom);
}

// Chat bubbles: consecutive lines of the same speaker become one bubble.
export function toBubbles(lines, liveFrom) {
    const bubbles = [];
    visibleLines(lines, liveFrom).forEach((line) => {
        const last = bubbles[bubbles.length - 1];
        if (last && last.role === line.role && last.src === line.src) {
            last.text = `${last.text} ${line.text}`.trim();
            last.to = line.time;
            last.partial = last.partial || !line.final;
            if (!last.name && line.name) last.name = line.name;
            return;
        }
        bubbles.push({
            id: line.id,
            src: line.src,
            role: line.role,
            name: line.name,
            text: line.text,
            from: line.time,
            to: line.time,
            partial: !line.final,
        });
    });
    return bubbles;
}

// An AI-assisted flag is { key, source, from, to, score }: `source` says which kind of line the times refer to.
// A candidate bubble is flagged when it is of that kind and its time span overlaps the flagged answer's.
export function flagForBubble(bubble, flags) {
    if (!bubble || bubble.role !== 'candidate') return null;
    return (
        (flags || []).find(
            (flag) =>
                flag &&
                flag.source === bubble.src &&
                Number.isFinite(flag.from) &&
                Number.isFinite(flag.to) &&
                bubble.from <= flag.to &&
                bubble.to >= flag.from
        ) || null
    );
}

// The newest candidate bubble's kind and time span — used when the backend could not say which saved messages
// formed the checked answer (it was built from the browser's own words).
export function latestCandidateSpan(lines, liveFrom) {
    const candidate = toBubbles(lines, liveFrom)
        .filter((bubble) => bubble.role === 'candidate')
        .pop();
    return candidate ? { source: candidate.src, from: candidate.from, to: candidate.to } : null;
}

// The recent finished real-time turns, in the shape the backend's context builder reads
// ({ role, message, dateTime }). Sent with a follow-up request so the question and answer come from the
// real-time text instead of the saved transcript, which is several seconds behind.
export function liveMessagesForRequest(lines, max = 40) {
    return (lines || [])
        .filter((line) => line.src === 'live' && line.final && line.text)
        .slice(-max)
        .map((line) => ({ role: line.role, message: line.text, dateTime: new Date(line.time).toISOString() }));
}
